import { useSelector } from "@app/react";
import { aiGatewayEnrollUrl, homeUrl } from "@app/routes";
import { selectUsersAsList } from "@app/users";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeroBgView } from "../layouts";
import { AptibleLogo, Button, Group, SignupMarketing } from "../shared";
import { trackSignupIntent } from "./getting-started-tuna-events";

type OptionKey = "deploy" | "ai-gateway";

const SECTIONS: {
  heading: string;
  options: {
    value: OptionKey;
    label: string;
    description: string;
  }[];
}[] = [
  {
    heading: "Managed Infrastructure",
    options: [
      {
        value: "deploy",
        label: "Deploy apps and databases",
        description: "Run workloads on secure, compliant infrastructure",
      },
    ],
  },
  {
    heading: "Managed AI",
    options: [
      {
        value: "ai-gateway",
        label: "LLM Gateway",
        description: "Proxy and govern LLM API calls with security controls",
      },
    ],
  },
];

const OptionCard = ({
  value,
  label,
  description,
  isSelected,
  onToggle,
}: {
  value: OptionKey;
  label: string;
  description: string;
  isSelected: boolean;
  onToggle: (key: OptionKey) => void;
}) => (
  <div
    className={[
      "rounded-lg border px-5 py-4",
      isSelected
        ? "border-indigo-500 bg-indigo-50"
        : "border-gray-200 bg-white",
    ].join(" ")}
  >
    <button
      type="button"
      onClick={() => onToggle(value)}
      className="w-full text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-gray-900">{label}</div>
          <div className="text-sm text-gray-500 mt-0.5">{description}</div>
        </div>
        <div
          className={[
            "flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors",
            isSelected ? "bg-indigo-500 border-indigo-500" : "border-gray-300",
          ].join(" ")}
        >
          {isSelected && (
            <svg
              className="w-3 h-3 text-white"
              viewBox="0 0 12 12"
              fill="none"
              aria-label="Selected"
              role="img"
            >
              <path
                d="M2 6l3 3 5-5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
      </div>
    </button>
  </div>
);

export const GettingStartedPage = () => {
  const [selected, setSelected] = useState<Set<OptionKey>>(new Set());
  const navigate = useNavigate();
  const users = useSelector(selectUsersAsList);
  const isOnlyOrgMember = users.length <= 1;

  useEffect(() => {
    if (users.length > 0 && !isOnlyOrgMember) {
      navigate(homeUrl(), { replace: true });
    }
  }, [users.length]);

  const toggle = (key: OptionKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleContinue = () => {
    trackSignupIntent({
      paas: selected.has("deploy"),
      ai: selected.has("ai-gateway"),
    });
    // DO NOT CHANGE THIS LINE BELOW UNLESS YOU KNOW WHAT YOU'RE DOING OR USERS MIGHT GET
    // STUCK ON FTUX. The signup flow is: signup -> verify email -> (NEW) getting-started -> <>
    // homeUrl() must be called here or users will get orphaned in FTUX.
    const aiOnly = selected.has("ai-gateway") && !selected.has("deploy");
    navigate(aiOnly ? aiGatewayEnrollUrl() : homeUrl());
  };

  return (
    <HeroBgView className="flex gap-6">
      <SignupMarketing />
      <div className="flex-1 mx-auto max-w-[500px]">
        <Group variant="vertical">
          <div className="mx-auto max-w-[570px] px-4 md:px-0">
            <Group>
              <div className="flex justify-center pt-[65px] pb-4">
                <AptibleLogo width={160} />
              </div>
            </Group>
          </div>
          <Group className="max-w-xl mx-auto px-4">
            <h2 className="mt-6 text-center text-3xl font-semibold text-gray-900">
              What are you looking to do?
            </h2>
            <div>
              <p className="text-sm text-gray-500 mt-0.5 text-center">
                Select all that apply
              </p>
            </div>

            <div className="flex flex-col gap-6">
              {SECTIONS.map((section) => (
                <div key={section.heading} className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      {section.heading}
                    </span>
                  </div>
                  {section.options.map((opt) => (
                    <OptionCard
                      key={opt.value}
                      value={opt.value}
                      label={opt.label}
                      description={opt.description}
                      isSelected={selected.has(opt.value)}
                      onToggle={toggle}
                    />
                  ))}
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3">
              <Button
                onClick={handleContinue}
                disabled={selected.size === 0}
                className="w-full"
              >
                Continue
              </Button>
            </div>
          </Group>
        </Group>
      </div>
    </HeroBgView>
  );
};
